import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { NavLink } from 'react-router-dom';
import BgColorContainer from '../../Layout/BgColorContainer';
import Spotlight from '../../ui/cinematic/Spotlight';
import PillLink from '../../ui/cinematic/PillLink';
import useScrollVideo from '../../../hooks/useScrollVideo';
import useCursorZone from '../../../hooks/useCursorZone';
import { useServicios } from '../../../cms/servicios';
import molecula from '../../../assets/home/molecula-7-grande.webp';
import grainCrema from '../../../assets/FondoHero.webp';
// Metaball del hero recodificado para scroll-scrub (ver useScrollVideo.js)
import heroVideo from '../../../assets/home/hero-scrub.mp4';
import heroPoster from '../../../assets/home/hero-poster.webp';
import { useAjustes } from '../../../cms/ajustes';
import { elemento } from '../../../cms/textos';

const EASE_EXPO = [0.16, 1, 0.3, 1];

const lineVariants = {
    hidden: { y: '110%' },
    visible: (i) => ({ y: '0%', transition: { duration: 1.2, delay: 0.25 + i * 0.12, ease: EASE_EXPO } }),
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (delay) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: EASE_EXPO } }),
};

const FILA_INFERIOR = 'flex flex-col gap-6 px-5 pb-8 md:flex-row md:items-end md:justify-between md:px-10 md:pb-10';

/** `hidden`: copia invisible que solo ocupa su lugar en la fila (ver capas gemelas abajo). */
function Bajada({ texto, as: Tag = 'p', hidden = false }) {
    return (
        <Tag aria-hidden={hidden || undefined} className={`max-w-sm text-sm leading-relaxed text-cream/75 md:text-base ${hidden ? 'invisible' : ''}`}>
            {texto}
        </Tag>
    );
}

// Desde md "Agendar reunión" vive en la barra flotante, que acompaña todo el
// scroll; acá aparece solo en mobile, donde la barra no lo muestra.
function Ctas({ textos, hidden = false }) {
    const { agenda } = useAjustes();
    return (
        <div aria-hidden={hidden || undefined} className={`flex flex-wrap gap-3 ${hidden ? 'invisible' : 'pointer-events-auto'}`}>
            <PillLink href="#servicios" variant="ghost">{textos.botonServicios}</PillLink>
            <PillLink href={agenda} external className="md:hidden">{textos.botonAgenda}</PillLink>
        </div>
    );
}

/**
 * Ceja en píldora crema, como "Agendar reunión": en difference sobre las burbujas
 * claras del video se perdía. `hidden`, igual que en `Bajada`. El pl suma el
 * tracking que queda colgando a la derecha de la última letra.
 */
function Ceja({ texto, as = 'p', hidden = false }) {
    const M = motion[as];
    return (
        <M
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.1}
            aria-hidden={hidden || undefined}
            className={`mb-6 whitespace-nowrap rounded-full bg-cream py-2 pl-[calc(1rem+0.2em)] pr-4 text-[11px] font-medium uppercase tracking-[0.2em] text-ink md:pl-[calc(1rem+0.3em)] md:tracking-[0.3em] shadow-[0_4px_24px_rgba(10,10,10,0.18)] md:mb-8 md:text-sm ${hidden ? 'invisible' : ''}`}
        >
            {texto}
        </M>
    );
}

/** Lo que aparece bajo el spotlight: la versión crema del hero del Figma. */
function CapaCrema() {
    return (
        <div className="absolute inset-0 bg-cream">
            <img src={grainCrema} alt="" className="absolute inset-0 h-full w-full object-cover" />
            {/* El div centra y GSAP anima solo la imagen: si animara el mismo
                elemento, fijaría el -50% en píxeles y la molécula se descentraría
                al cambiar el tamaño de la ventana. */}
            <div className="absolute left-1/2 top-1/2 w-[80vw] max-w-[1100px] -translate-x-1/2 -translate-y-1/2 md:w-[58vw]">
                <img src={molecula} alt="" className="hero-molecula w-full" />
            </div>
        </div>
    );
}

/** Los textos vienen de "Páginas → Inicio" en Sanity (Home.jsx). */
function Hero({ textos }) {
    const sectionRef = useRef(null);
    const videoRef = useScrollVideo({ triggerRef: sectionRef });
    const cursorZone = useCursorZone('dark');

    // Coreografía de salida: el texto sube y se apaga, el video se acerca y
    // termina fundido en el negro de la sección siguiente.
    useGSAP(() => {
        const tl = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: { trigger: sectionRef.current, start: 'top top', end: 'bottom bottom', scrub: true },
        });
        tl.to('.hero-media', { scale: 1.12, duration: 1 }, 0)
            .to('.hero-bottom', { opacity: 0, y: 40, duration: 0.3 }, 0)
            .to('.hero-copy', { yPercent: -35, opacity: 0, duration: 0.45 }, 0.35)
            .to('.hero-molecula', { rotate: 25, scale: 1.15, duration: 1 }, 0)
            .to('.hero-fade', { opacity: 1, duration: 0.25 }, 0.75);
    }, { scope: sectionRef });

    const pills = useServicios().servicios.slice(0, 4);
    // El elemento de la primera línea es el del titular entero: las dos líneas
    // son un solo título que se lee de corrido
    const Titular = elemento(textos, 'linea1', 'h1');
    const Linea2 = motion[elemento(textos, 'linea2', 'span')];

    return (
        <BgColorContainer>
            <section ref={sectionRef} className="relative h-[250svh] bg-ink" aria-label="Inicio" {...cursorZone}>
                <Spotlight
                    className="sticky top-0 h-svh w-full overflow-hidden"
                    reveal={<CapaCrema />}
                    revealClassName="z-10"
                >
                    {/* Burbujas: el video, que solo avanza con el scroll. Sin overlay. */}
                    <div className="hero-media absolute inset-0 z-0">
                        <video
                            ref={videoRef}
                            src={heroVideo}
                            poster={heroPoster}
                            muted
                            playsInline
                            preload="auto"
                            aria-hidden="true"
                            className="h-full w-full object-cover"
                        />
                    </div>

                    {/* Copy: mix-blend-difference lo deja crema sobre negro y negro sobre el spotlight */}
                    <div className="hero-copy pointer-events-none relative z-20 flex h-full flex-col items-center justify-center px-5 text-center text-cream mix-blend-difference">
                        <Ceja texto={textos.ceja} as={elemento(textos, 'ceja', 'p')} hidden />
                        <Titular className="leading-[0.95]">
                            {/* El tamaño va en la máscara para que su pb (en em) se mida con
                                la letra grande: con el del h1 cortaba la cola de la "g" */}
                            <span className="-mb-[0.12em] block overflow-hidden pb-[0.2em] font-serif text-[15vw] italic md:text-[10.5vw] xl:text-[9.5rem]">
                                <motion.span variants={lineVariants} initial="hidden" animate="visible" custom={0} className="block">
                                    {textos.linea1}
                                </motion.span>
                            </span>
                            <span className="-mb-[0.1em] block overflow-hidden pb-[0.2em] text-[8.5vw] md:text-[5.2vw] xl:text-[4.75rem]">
                                <Linea2 variants={lineVariants} initial="hidden" animate="visible" custom={1} className="block font-medium tracking-tight">
                                    {textos.linea2}
                                </Linea2>
                            </span>
                        </Titular>
                    </div>

                    {/* Gemela sin difference para la ceja (una píldora crema en
                        difference se invertiría): mismo layout, el titular solo
                        reserva su lugar. GSAP mueve las dos por la clase .hero-copy. */}
                    <div aria-hidden="true" className="hero-copy pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center px-5 text-center">
                        <Ceja texto={textos.ceja} as={elemento(textos, 'ceja', 'p')} />
                        <p className="invisible leading-[0.95]">
                            <span className="-mb-[0.12em] block pb-[0.2em] font-serif text-[15vw] italic md:text-[10.5vw] xl:text-[9.5rem]">{textos.linea1}</span>
                            <span className="-mb-[0.1em] block pb-[0.2em] text-[8.5vw] md:text-[5.2vw] xl:text-[4.75rem]">
                                <span className="block font-medium tracking-tight">{textos.linea2}</span>
                            </span>
                        </p>
                    </div>

                    {/* Capas inferiores: GSAP anima el contenedor (.hero-bottom) y
                        framer-motion el hijo, para que no se pisen la opacidad.
                        Los botones van en vidrio oscuro y sin difference: el spotlight
                        sigue al cursor, así que en hover siempre queda debajo del
                        botón y lo invertía (y las burbujas del video se veían encima). */}
                    <div className="hero-bottom absolute inset-x-0 bottom-36 z-20 hidden justify-center px-5 lg:flex">
                        <motion.nav
                            variants={fadeUp}
                            initial="hidden"
                            animate="visible"
                            custom={0.9}
                            aria-label="Servicios destacados"
                            className="flex gap-2"
                        >
                            {pills.map((s) => (
                                <NavLink
                                    key={s.url}
                                    to={`/services/${s.url}`}
                                    className="rounded-full bg-ink/60 px-4 py-2 text-sm text-cream/85 ring-1 ring-inset ring-cream/15 backdrop-blur-md transition-colors duration-300 hover:bg-cream hover:text-ink"
                                >
                                    {s.nombre}
                                </NavLink>
                            ))}
                        </motion.nav>
                    </div>

                    {/* La bajada sí va en difference, así que bajada y botones viven en
                        dos capas gemelas con la misma fila: en cada una el otro
                        elemento está invisible y solo reserva su lugar. */}
                    <div className="hero-bottom pointer-events-none absolute inset-x-0 bottom-0 z-20 mix-blend-difference">
                        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1.05} className={FILA_INFERIOR}>
                            <Bajada texto={textos.bajada} as={elemento(textos, 'bajada', 'p')} />
                            <Ctas textos={textos} hidden />
                        </motion.div>
                    </div>

                    <div className="hero-bottom pointer-events-none absolute inset-x-0 bottom-0 z-20">
                        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1.05} className={FILA_INFERIOR}>
                            <Bajada texto={textos.bajada} as={elemento(textos, 'bajada', 'p')} hidden />
                            <Ctas textos={textos} />
                        </motion.div>
                    </div>

                    {/* Fundido final hacia el negro de la sección siguiente */}
                    <div className="hero-fade pointer-events-none absolute inset-0 z-30 bg-ink opacity-0" />
                </Spotlight>
            </section>
        </BgColorContainer>
    );
}

export default Hero;
